// Einfache Zeilenliste für Sheets (BottomNav „More“, Konto-Menü mobil): Zeile 48 mit Icon-Kreis 40.
import { type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';

export interface ListMenuItem {
  id: string;
  label: string;
  icon?: PhosphorIcon;
  /** Eigenes Bild links statt Icon (Avatar, Monogramm). */
  leading?: ReactNode;
  description?: string;
  href?: string;
  reload?: boolean;
  onSelect?: () => void;
  trailing?: ReactNode;
  active?: boolean;
  tone?: 'default' | 'danger';
}

export interface ListMenuSection {
  id: string;
  label?: string;
  items: readonly ListMenuItem[];
}

export function ListMenu({
  sections,
  onDone,
  className,
}: {
  sections: readonly ListMenuSection[];
  /** Nach jeder Wahl (Sheet schließen). */
  onDone?: () => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {sections.map((section) => (
        <section key={section.id} aria-label={section.label}>
          {section.label ? (
            <p className="px-3 pt-2 pb-1 text-chip text-fg-tertiary">{section.label}</p>
          ) : null}
          <ul>
            {section.items.map((item) => (
              <li key={item.id}>
                <ListMenuRow item={item} onDone={onDone} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ListMenuRow({ item, onDone }: { item: ListMenuItem; onDone?: () => void }) {
  const Glyph = item.icon;
  const classes = cn(
    'flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-body transition-colors duration-(--bar-dur-fast) hover:bg-card-hover',
    item.tone === 'danger' ? 'text-danger-fg' : 'text-fg',
    item.active && 'bg-selection',
  );
  const body = (
    <>
      {item.leading ? (
        // Gleiche Einrückung wie Zeilen mit Icon-Kreis 40 (Text immer bei x 72).
        <span className="flex size-10 shrink-0 items-center justify-center">{item.leading}</span>
      ) : Glyph ? (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-icon-circle">
          <Glyph size={20} aria-hidden />
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{item.label}</span>
        {item.description ? (
          <span className="truncate text-label text-fg-secondary">{item.description}</span>
        ) : null}
      </span>
      {item.trailing}
    </>
  );
  if (item.href) {
    return (
      <RouterLink
        href={item.href}
        reload={item.reload}
        aria-current={item.active ? 'page' : undefined}
        className={classes}
        onClick={() => {
          item.onSelect?.();
          onDone?.();
        }}
      >
        {body}
      </RouterLink>
    );
  }
  if (!item.onSelect) return <div className={classes}>{body}</div>;
  return (
    <button
      type="button"
      className={classes}
      onClick={() => {
        item.onSelect?.();
        onDone?.();
      }}
    >
      {body}
    </button>
  );
}
